import { useSessionLandingPage } from '../session';

import { includesPlant, plantsWhere } from './domain/decide';
import { useAccess } from './hooks';
import { DASHBOARD_RULES } from './ledger';
import { DASHBOARD_HOME, HOME_PATH, ISSUES_HOME, plantDashboard } from './paths';
import { useCanVisit } from './routing/canVisit';
import { linksOf } from './sidebar/sidebarItems';
import { useSidebarState } from './sidebar/useSidebarItems';
import type { SidebarState } from './sidebar/useSidebarItems';
import type { Landing, MeAccess } from './types';

const firstDashboardPlant = (me: MeAccess): string | null => {
  const where = plantsWhere(me, DASHBOARD_RULES.view);
  return me.plantIds.find((plantId) => includesPlant(where, plantId)) ?? null;
};

export const landingCandidate = (me: MeAccess, landing: Landing | null): string | null => {
  switch (landing) {
    case null:
      return null;
    case 'ISSUES_OVERDUE_FIRST':
    case 'ISSUES_OP_FIRST':
      return ISSUES_HOME;
    case 'MULTI_PLANT_OVERVIEW':
    case 'PORTFOLIO_DASHBOARD':
    case 'MONITORING_OVERSIGHT':
      return DASHBOARD_HOME;
    case 'PLANT_VIEW': {
      const plant = firstDashboardPlant(me);
      return plant === null ? DASHBOARD_HOME : plantDashboard(plant);
    }
    default: {
      const unhandled: never = landing;
      throw new Error(`Landing without a home page: ${JSON.stringify(unhandled)}`);
    }
  }
};

const isOpenPath =
  (canVisit: (to: string) => boolean) =>
  (path: string | null): path is string =>
    path !== null && path !== '' && path !== HOME_PATH && canVisit(path);

export type LandingPath =
  | { readonly kind: 'open'; readonly path: string }
  | { readonly kind: 'loading' }
  | { readonly kind: 'failed'; readonly retry: () => void }
  | { readonly kind: 'nothing' };

const LOADING: LandingPath = { kind: 'loading' };

const NOTHING: LandingPath = { kind: 'nothing' };

const openOrNothing = (path: string | undefined): LandingPath => (path === undefined ? NOTHING : { kind: 'open', path });

const resolveLanding = (me: MeAccess, canVisit: (to: string) => boolean, sidebar: SidebarState, sessionLanding: string | null): LandingPath => {
  const isOpen = isOpenPath(canVisit);
  // Sign-in names each person's own page (their chosen default, else what they may open); the role's landing only fills in.
  const named = [sessionLanding, landingCandidate(me, me.landing)].find(isOpen);
  if (named !== undefined) return { kind: 'open', path: named };
  switch (sidebar.status) {
    case 'ready':
      return openOrNothing(linksOf(sidebar.items).find(isOpen));
    case 'loading':
      return LOADING;
    case 'failed':
      return { kind: 'failed', retry: sidebar.retry };
    default: {
      const unhandled: never = sidebar;
      throw new Error(`Sidebar state without a landing: ${JSON.stringify(unhandled)}`);
    }
  }
};

export const useLandingPath = (): LandingPath => {
  const me = useAccess();
  const canVisit = useCanVisit();
  const sidebar = useSidebarState();
  const sessionLanding = useSessionLandingPage();
  return resolveLanding(me, canVisit, sidebar, sessionLanding);
};

export const useLandingResolver = (): (() => LandingPath) => {
  const me = useAccess();
  const canVisit = useCanVisit();
  const sidebar = useSidebarState();
  const sessionLanding = useSessionLandingPage();
  return () => resolveLanding(me, canVisit, sidebar, sessionLanding);
};
